import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Home, Users, Calendar, Settings, Star, Crown, 
  Mountain, Sparkles, Heart, Play, Volume2, ArrowRight,
  FileText, MapPin, Gift, Edit, Eye, Music, Coffee,
  Bed, Utensils, TreePine, Sun, Moon, BookOpen
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import SonicExperience from './SonicExperience';

const GuildHaus = ({ profileData, onNavigateToProjects, onNavigateToSecretHistory, onNavigateToRetreat }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [showSonicExperience, setShowSonicExperience] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState({
    interests: [],
    availability: '',
    special_needs: '',
    retreat_interest: false
  });
  const [mairyChat, setMairyChat] = useState([
    {
      id: 'welcome',
      sender: 'M.ai.ry',
      content: `Welcome to your Guild Haus, ${profileData?.giftName || 'Gift'} Bearer. I'm here to help coordinate your Federation experience.`,
      timestamp: new Date()
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [realTimeOpportunities, setRealTimeOpportunities] = useState([
    {
      id: 'retreat_sept',
      type: 'retreat',
      title: 'Autumn Threshold Retreat',
      description: 'September gathering for families navigating transitions',
      location: 'Defiance Springs',
      urgency: 'high'
    }
  ]);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.25) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  const sendMairyMessage = () => {
    if (!chatInput.trim()) return;
    
    const userMessage = {
      id: `msg_${Date.now()}`,
      sender: 'You',
      content: chatInput,
      timestamp: new Date()
    };
    
    setMairyChat(prev => [...prev, userMessage]);
    
    // M.ai.ry response with real-time opportunity updates
    setTimeout(() => {
      const responses = [
        "I sense your family's needs shifting. Let me update your opportunities...",
        "New retreat openings have appeared. Shall I add you to the priority list?",
        "Another family with your gift profile has joined. Connection opportunities emerging...",
        "Based on your thresholds, I'm seeing perfect matches in the Federation network."
      ];
      
      const response = {
        id: `response_${Date.now()}`,
        sender: 'M.ai.ry',
        content: responses[Math.floor(Math.random() * responses.length)],
        timestamp: new Date()
      };
      
      setMairyChat(prev => [...prev, response]);
      
      // Update real-time opportunities
      const newOpportunity = {
        id: `opp_${Date.now()}`,
        type: 'match',
        title: 'Family Match Available',
        description: `Another ${profileData?.giftName || 'Gift'} Bearer seeks collaboration`,
        urgency: 'medium'
      };
      
      setRealTimeOpportunities(prev => [newOpportunity, ...prev.slice(0, 4)]);
    }, 1000);
    
    setChatInput('');
  };

  const federationBenefits = [
    {
      icon: Mountain,
      title: 'Sacred Retreats',
      description: 'Complimentary stays at consciousness expansion properties worldwide',
      action: 'Explore Locations'
    },
    {
      icon: Users,
      title: 'Family Matching',
      description: 'Connect with families navigating similar thresholds',
      action: 'Find Matches'
    },
    {
      icon: Crown,
      title: 'Master Guidance',
      description: 'Access to skilled facilitators and wisdom keepers',
      action: 'Browse Masters'
    },
    {
      icon: Heart,
      title: 'Remedy Council',
      description: 'Sacred conflict resolution for family healing',
      action: 'Request Council'
    }
  ];

  const interestOptions = [
    'Family healing ceremonies',
    'Consciousness expansion workshops', 
    'Regenerative living practices',
    'Children\'s wisdom circles',
    'Elder care collaboration',
    'Creative expression therapy',
    'Grief & transition support',
    'Celebration & ritual creation'
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* Beautiful Cosmic Pastoral Background */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/a1qoxqhi_ChatGPT%20Image%20Sep%2029%2C%202025%20at%2012_30_09%20AM.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Grant Spiral Magic */}
      <div className="fixed inset-0 pointer-events-none opacity-20">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(18) rotate(${spiralPhase * 0.4}deg)`,
            filter: 'blur(3px) brightness(1.5) saturate(1.8)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={200} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-black/70" />

      <div className="relative z-10 p-6 md:p-8 max-w-7xl mx-auto">
        
        {/* Guild Haus Header */}
        <div className="text-center mb-8">
          <h1 
            className="text-4xl md:text-6xl font-bold text-yellow-200 mb-4"
            style={{
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 50px rgba(255, 215, 0, 0.8)'
            }}
          >
            🏰 Guild Haus
          </h1>
          
          <p className="text-yellow-300 text-lg max-w-3xl mx-auto leading-relaxed">
            Welcome to your personal Federation sanctuary, {profileData.giftName} Bearer. 
            Your threshold journey through {profileData.name.toLowerCase()} has brought you to this sacred space of belonging and possibility.
          </p>
        </div>

        {/* Profile Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          
          {/* Your Federation Profile */}
          <Card className="bg-black/60 backdrop-blur-xl border border-yellow-400/30">
            <CardHeader>
              <CardTitle className="text-yellow-300 flex items-center space-x-2">
                <Crown className="w-5 h-5" />
                <span>Your Federation Profile</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                  <Gift className="w-10 h-10 text-yellow-400" />
                </div>
                <h3 className="text-white font-bold text-xl">{profileData.giftName} Bearer</h3>
                <p className="text-yellow-200 text-sm">{profileData.giftDescription}</p>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/70">Threshold:</span>
                  <span className="text-yellow-300">{profileData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Path:</span>
                  <span className="text-yellow-300">{profileData.userType === 'giveth' ? 'Giver' : 'Seeker'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Status:</span>
                  <Badge className="bg-green-500/30 text-green-200">Active Member</Badge>
                </div>
              </div>
              
              <Button
                onClick={() => {
                  // Show additional info form
                  const newInterests = prompt('What additional interests do you have? (comma separated)');
                  if (newInterests) {
                    setAdditionalInfo(prev => ({
                      ...prev,
                      interests: newInterests.split(',').map(i => i.trim())
                    }));
                  }
                }}
                className="w-full bg-yellow-600 hover:bg-yellow-700"
                size="sm"
              >
                <Edit className="w-4 h-4 mr-2" />
                Add More Info
              </Button>
            </CardContent>
          </Card>

          {/* Federation Benefits */}
          <Card className="bg-black/60 backdrop-blur-xl border border-cyan-400/30">
            <CardHeader>
              <CardTitle className="text-cyan-300 flex items-center space-x-2">
                <Sparkles className="w-5 h-5" />
                <span>Federation Benefits</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {federationBenefits.map((benefit, index) => {
                  const IconComponent = benefit.icon;
                  return (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-black/20 rounded-lg">
                      <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-cyan-300" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{benefit.title}</h4>
                        <p className="text-cyan-200 text-xs">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-black/60 backdrop-blur-xl border border-purple-400/30">
            <CardHeader>
              <CardTitle className="text-purple-300 flex items-center space-x-2">
                <Star className="w-5 h-5" />
                <span>Quick Actions</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Button
                  onClick={onNavigateToSecretHistory}
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-left relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <div className="relative flex items-center">
                    <Play className="w-4 h-4 mr-3 animate-pulse" />
                    <div>
                      <div className="font-bold">✨ Enter the Game</div>
                      <div className="text-xs opacity-90">Elucidate next steps & find teams for your holiday</div>
                    </div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => setAdditionalInfo(prev => ({...prev, retreat_interest: true}))}
                  className="w-full bg-green-600 hover:bg-green-700 text-left"
                >
                  <Mountain className="w-4 h-4 mr-3" />
                  <div>
                    <div className="font-medium">Retreat Interest</div>
                    <div className="text-xs opacity-80">Join family gatherings</div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => setShowSonicExperience(!showSonicExperience)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-left"
                >
                  <Volume2 className="w-4 h-4 mr-3" />
                  <div>
                    <div className="font-medium">Cosmic Audio</div>
                    <div className="text-xs opacity-80">Sonic consciousness experience</div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => alert('Family Organizer Tech - Advanced project management for families coming soon!')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-left"
                >
                  <Home className="w-4 h-4 mr-3" />
                  <div>
                    <div className="font-medium">Family Organizer</div>
                    <div className="text-xs opacity-80">Tech solution for families</div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Real-Time Opportunities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* M.ai.ry Personal Chat */}
          <Card className="bg-black/60 backdrop-blur-xl border border-pink-400/30">
            <CardHeader>
              <CardTitle className="text-pink-300 flex items-center space-x-2">
                <Crown className="w-5 h-5" />
                <span>Chat with M.ai.ry</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Chat Messages */}
                <div className="bg-black/20 rounded-lg p-3 max-h-48 overflow-y-auto space-y-3">
                  {mairyChat.map(msg => (
                    <div key={msg.id} className={`${msg.sender === 'M.ai.ry' ? 'text-pink-300' : 'text-cyan-300'}`}>
                      <div className="text-xs font-mono">{msg.sender}:</div>
                      <div className="text-sm">{msg.content}</div>
                    </div>
                  ))}
                </div>
                
                {/* Chat Input */}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        sendMairyMessage();
                      }
                    }}
                    placeholder="Chat with M.ai.ry..."
                    className="flex-1 p-2 bg-black/30 border border-pink-400/30 rounded text-white text-sm"
                  />
                  <Button
                    onClick={sendMairyMessage}
                    size="sm"
                    className="bg-pink-600 hover:bg-pink-700"
                  >
                    Send
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Real-Time Opportunities */}
          <Card className="bg-black/60 backdrop-blur-xl border border-green-400/30">
            <CardHeader>
              <CardTitle className="text-green-300 flex items-center space-x-2">
                <Sparkles className="w-5 h-5" />
                <span>Live Opportunities</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {realTimeOpportunities.map(opp => (
                  <div key={opp.id} className="bg-black/20 rounded-lg p-3 border border-green-400/20">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium text-sm">{opp.title}</h4>
                      <Badge className={
                        opp.urgency === 'high' ? 'bg-red-500/30 text-red-200' :
                        opp.urgency === 'medium' ? 'bg-yellow-500/30 text-yellow-200' :
                        'bg-green-500/30 text-green-200'
                      }>
                        {opp.urgency}
                      </Badge>
                    </div>
                    <p className="text-green-200 text-xs">{opp.description}</p>
                    {opp.location && (
                      <p className="text-green-300/70 text-xs mt-1">📍 {opp.location}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Federation Menu */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          {/* Real World Retreats */}
          <Card className="bg-green-900/40 backdrop-blur-xl border border-green-400/30 hover:bg-green-900/60 transition-all cursor-pointer group">
            <CardContent className="p-6 text-center">
              <Mountain className="w-12 h-12 mx-auto text-green-300 mb-4" />
              <h3 className="text-green-200 font-bold text-lg mb-2">Sacred Retreats</h3>
              <p className="text-green-300 text-sm mb-4">
                Real-world family gathering retreats for consciousness expansion and healing
              </p>
              <Button 
                onClick={onNavigateToRetreat}
                className="bg-green-600 hover:bg-green-700 w-full"
                size="sm"
              >
                Explore Locations
              </Button>
            </CardContent>
          </Card>

          {/* Federation Info */}
          <Card className="bg-purple-900/40 backdrop-blur-xl border border-purple-400/30 hover:bg-purple-900/60 transition-all cursor-pointer group">
            <CardContent className="p-6 text-center">
              <Users className="w-12 h-12 mx-auto text-purple-300 mb-4" />
              <h3 className="text-purple-200 font-bold text-lg mb-2">The Guild</h3>
              <p className="text-purple-300 text-sm mb-4">
                Learn about the Federation of families and our reciprocity-based community
              </p>
              <Button 
                onClick={() => alert('Federation Guild information: A sacred network of families supporting each other through life\'s transitions with an economy of gifts and mutual aid.')}
                className="bg-purple-600 hover:bg-purple-700 w-full"
                size="sm"
              >
                Learn More
              </Button>
            </CardContent>
          </Card>

          {/* Project Salons */}
          <Card className="bg-cyan-900/40 backdrop-blur-xl border border-cyan-400/30 hover:bg-cyan-900/60 transition-all cursor-pointer group">
            <CardContent className="p-6 text-center">
              <FileText className="w-12 h-12 mx-auto text-cyan-300 mb-4" />
              <h3 className="text-cyan-200 font-bold text-lg mb-2">Project Salons</h3>
              <p className="text-cyan-300 text-sm mb-4">
                Advanced project management tools with todos, calendar, and collaboration
              </p>
              <Button 
                onClick={onNavigateToProjects}
                className="bg-cyan-600 hover:bg-cyan-700 w-full"
                size="sm"
              >
                Enter Salons
              </Button>
            </CardContent>
          </Card>

          {/* Secret History Gameplay */}
          <Card className="bg-orange-900/40 backdrop-blur-xl border border-orange-400/30 hover:bg-orange-900/60 transition-all cursor-pointer group">
            <CardContent className="p-6 text-center">
              <Play className="w-12 h-12 mx-auto text-orange-300 mb-4" />
              <h3 className="text-orange-200 font-bold text-lg mb-2">Epic Gameplay</h3>
              <p className="text-orange-300 text-sm mb-4">
                Enter The Secret History to elucidate solutions through consciousness play
              </p>
              <Button 
                onClick={onNavigateToSecretHistory}
                className="bg-orange-600 hover:bg-orange-700 w-full"
                size="sm"
              >
                Begin Quest
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Sonic Experience Side Game */}
        {showSonicExperience && (
          <Card className="bg-purple-900/40 backdrop-blur-xl border border-purple-400/30 mb-8">
            <CardHeader>
              <CardTitle className="text-purple-300 flex items-center space-x-2">
                <Music className="w-5 h-5" />
                <span>Cosmic Audio Experience</span>
                <Button
                  onClick={() => setShowSonicExperience(false)}
                  variant="ghost"
                  size="sm"
                  className="text-purple-400 hover:text-purple-300"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <SonicExperience 
                  player={profileData}
                  onNavigateToGame={() => {}}
                  onNavigateHome={() => {}}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Information */}
        <Card className="bg-black/60 backdrop-blur-xl border border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Settings className="w-5 h-5" />
              <span>Federation Preferences</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Interests */}
              <div>
                <h4 className="text-white font-medium mb-3">Areas of Interest</h4>
                <div className="space-y-2">
                  {interestOptions.map(option => (
                    <button
                      key={option}
                      onClick={() => {
                        setAdditionalInfo(prev => ({
                          ...prev,
                          interests: prev.interests.includes(option) 
                            ? prev.interests.filter(i => i !== option)
                            : [...prev.interests, option]
                        }));
                      }}
                      className={`w-full text-left p-2 rounded border text-sm transition-all ${
                        additionalInfo.interests.includes(option)
                          ? 'bg-yellow-500/20 border-yellow-400/50 text-yellow-200'
                          : 'bg-black/20 border-white/20 text-white/70 hover:bg-black/40'
                      }`}
                    >
                      {additionalInfo.interests.includes(option) && '✓ '}{option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Availability & Notes */}
              <div className="space-y-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Availability for Gatherings</label>
                  <select 
                    value={additionalInfo.availability}
                    onChange={(e) => setAdditionalInfo(prev => ({...prev, availability: e.target.value}))}
                    className="w-full p-3 bg-black/30 border border-white/20 rounded text-white"
                  >
                    <option value="">Select availability...</option>
                    <option value="weekends_only">Weekends Only</option>
                    <option value="flexible">Flexible Schedule</option>
                    <option value="limited">Limited Availability</option>
                    <option value="high">High Availability</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-white font-medium mb-2 block">Special Needs or Considerations</label>
                  <textarea
                    value={additionalInfo.special_needs}
                    onChange={(e) => setAdditionalInfo(prev => ({...prev, special_needs: e.target.value}))}
                    placeholder="Accessibility needs, dietary restrictions, childcare requirements..."
                    className="w-full p-3 bg-black/30 border border-white/20 rounded text-white placeholder-white/50 text-sm h-24 resize-none"
                  />
                </div>
                
                <Button
                  onClick={() => {
                    alert(`✅ Federation Preferences Updated!\n\nInterests: ${additionalInfo.interests.length} selected\nAvailability: ${additionalInfo.availability || 'Not specified'}\nSpecial needs: ${additionalInfo.special_needs ? 'Noted' : 'None'}\n\nYour profile helps us create better matches and experiences.`);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GuildHaus;